import React from 'react'
import { Text, getPaletteColor } from 'pcln-design-system'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { unformat } from 'currency-formatter'

import priceFormatter from '../../util/price-formatter'

const StyledDollars = styled(Text)`
  display: flex;
  color: ${(props) => getPaletteColor(props.color)(props)};
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  ${(props) => (props.isShelf ? 'justify-content: center;' : '')}

  [data-test='regular-dollars'] {
    display: none;
  }

  [data-test='rounded-dollars'] {
    display: inline;
  }

  ${(props) => props.theme.flyQueries.tablet} {
    font-size: 22px;
    line-height: 22px;
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 24px;
    line-height: 24px;

    [data-test='regular-dollars'] {
      display: inline;
    }

    [data-test='rounded-dollars'] {
      display: none;
    }
  }
`

const StyledCents = styled.sup`
  display: none;
  font-weight: 400;
  font-size: ${(props) => props.theme.fontSizes[0]}px;
  line-height: ${(props) => props.theme.fontSizes[0]}px;
  vertical-align: super;
  margin-top: 4px;
  padding-left: 2px;

  .decimal {
    margin-right: 0.5px;
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    display: inline-block;
    font-size: ${(props) => props.theme.fontSizes[1]}px;
  }
`

const CurrencySymbol = styled.span`
  font-size: 18px;
  line-height: 18px;

  ${(props) => props.theme.flyQueries.desktoplg} {
    vertical-align: super;
    font-size: ${(props) => props.theme.fontSizes[1]}px;
    line-height: ${(props) => props.theme.fontSizes[3]}px;
  }
`

export default function DisplayPrice(props) {
  const { price: priceObj, prepend, omitCents, color, isShelf } = props
  const { symbol, dollar, cents, decimalSeparator, currencyCode } = priceFormatter(priceObj)
  const dollarOnly = parseInt(unformat(dollar, { code: currencyCode }), 10)
  const dollarCeiling = !cents || cents === '00' ? dollarOnly : dollarOnly + 1
  const { dollar: roundedDollar } = priceFormatter({
    price: dollarCeiling,
    currencyCode,
    isRounded: true,
  })

  return (
    <StyledDollars isShelf={isShelf} color={color} data-testid='pricing-info-rtl' data-test='pricing-info'>
      {prepend}
      {<CurrencySymbol data-test='currency-symbol'>{symbol}</CurrencySymbol>}
      <span data-test='rounded-dollars'>{roundedDollar}</span>
      <span data-test='regular-dollars'>{dollar}</span>
      {!omitCents && cents && (
        <StyledCents data-test='cents'>
          {decimalSeparator}
          {cents}
        </StyledCents>
      )}
    </StyledDollars>
  )
}

StyledDollars.defaultProps = {
  color: 'secondary.base',
}

DisplayPrice.defaultProps = {
  color: 'secondary.base',
}

DisplayPrice.propTypes = {
  price: PropTypes.shape({
    cents: PropTypes.string,
    dollar: PropTypes.string.isRequired,
    currencyCode: PropTypes.string,
  }).isRequired,
  prepend: PropTypes.string,
  omitCents: PropTypes.bool,
  color: PropTypes.string,
  isShelf: PropTypes.bool,
}
